import type { ReactNode } from 'react'

import { cx } from './cx.ts'
import { useDisabled, type DisabledState } from './disabled.tsx'
import { Icon, type IconName } from './Icon.tsx'

import './button.css'

/**
 * 버튼 — 와이어프레임 `btn()` 1029행.
 *
 * 와이어프레임은 `<div class="btn">` 이다. 여기서는 **반드시 `<button>`** 이다(§11-4-1).
 *
 * 🔴 비활성 규약이 이 컴포넌트의 존재 이유다.
 *   - `disabled: true` 면 `disabledReason` 이 **타입으로 강제**된다. 사유 없는 비활성이 안 생긴다
 *   - 사유는 툴팁(`title`) + 시각적으로 숨긴 텍스트(`aria-describedby`)로만 나간다.
 *     화면 본문에 조건을 글로 쓰지 않는다(§1-1)
 *   - 권한이 없어도 **숨기지 않는다**. 집행은 서버 403 `INSUFFICIENT_ROLE` 이다(README §5-6)
 */
export type ButtonVariant = 'primary' | 'secondary' | 'danger'
export type ButtonSize = 'md' | 'sm' | 'tall' | 'pill'

type ButtonBase = {
  children: ReactNode
  onClick?: (() => void) | undefined
  /** 기본 'secondary'. primary = 검정 채움 */
  variant?: ButtonVariant | undefined
  /** 기본 'md'(44px). sm 34 · tall 60 · pill 30 알약 */
  size?: ButtonSize | undefined
  /** 눌림 상태(토글·선택). `aria-pressed` 로 나간다 — 색만으로 구분하지 않는다 */
  selected?: boolean | undefined
  /**
   * 라벨 아래 보조문구. 와이어프레임 `btn()` 의 `sub` 다.
   * ⚠ 비활성 사유를 여기 넣지 마라. 사유는 `disabledReason` 이고 툴팁 전용이다
   */
  hint?: string | undefined
  type?: 'button' | 'submit' | undefined
  block?: boolean | undefined
  'aria-label'?: string | undefined
}

export type ButtonProps = ButtonBase & DisabledState

const SIZE_CLASS: Record<ButtonSize, string> = {
  md: '',
  sm: 'a-btn--sm',
  tall: 'a-btn--tall',
  pill: 'a-btn--pill',
}

export function Button({
  children,
  onClick,
  variant = 'secondary',
  size = 'md',
  selected,
  hint,
  type = 'button',
  block,
  disabled,
  disabledReason,
  'aria-label': ariaLabel,
}: ButtonProps) {
  const { isDisabled, ariaProps, reasonNode } = useDisabled(disabled, disabledReason)

  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={selected}
      className={cx(
        'a-btn',
        variant !== 'secondary' && `a-btn--${variant}`,
        SIZE_CLASS[size],
        block === true && 'a-btn--block',
      )}
      onClick={isDisabled ? undefined : onClick}
      type={type}
      {...ariaProps}
    >
      {children}
      {hint !== undefined && hint !== '' ? <small className="a-btn__hint">{hint}</small> : null}
      {reasonNode}
    </button>
  )
}

export type IconButtonProps = ButtonProps & { icon: IconName }

/**
 * 아이콘 버튼 — 와이어프레임 `ibtn()` 1053행. 아이콘 위 · 라벨 아래 64px.
 * 제어 화면과 패드가 쓴다. 비활성 규약은 `Button` 과 완전히 같다.
 */
export function IconButton({
  icon,
  children,
  onClick,
  variant = 'secondary',
  selected,
  hint,
  type = 'button',
  block,
  disabled,
  disabledReason,
  'aria-label': ariaLabel,
}: IconButtonProps) {
  const { isDisabled, ariaProps, reasonNode } = useDisabled(disabled, disabledReason)

  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={selected}
      className={cx(
        'a-btn',
        'a-btn--icon',
        variant !== 'secondary' && `a-btn--${variant}`,
        block === true && 'a-btn--block',
      )}
      onClick={isDisabled ? undefined : onClick}
      type={type}
      {...ariaProps}
    >
      <Icon name={icon} size="xl" />
      <span>{children}</span>
      {hint !== undefined && hint !== '' ? <small className="a-btn__hint">{hint}</small> : null}
      {reasonNode}
    </button>
  )
}
